import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    // Check if permanently dismissed (user installed the app)
    if (localStorage.getItem('pwa-installed') === 'true') {
      setIsInstalled(true);
      return;
    }

    // Check if already installed (running in standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      // Set lifetime flag since they have it installed
      localStorage.setItem('pwa-installed', 'true');
      return;
    }

    // Check if dismissed recently (7 days for "Maybe later")
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show prompt for all users after delay (for browsers that don't support beforeinstallprompt)
    // This includes Safari, Firefox, and headless browsers
    const showTimer = setTimeout(() => {
      if (!deferredPrompt) {
        setShowPrompt(true);
      }
    }, 3000);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      // Set lifetime flag - never show prompt again
      localStorage.setItem('pwa-installed', 'true');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(showTimer);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // For iOS, just close the prompt (user needs to do it manually)
      setShowPrompt(false);
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] animate-fadeIn"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[92%] max-w-sm animate-scaleIn">
        <div className="relative bg-gradient-to-b from-[#1a1a2e] to-[#0d0d1a] border border-[#6D4AFF]/30 rounded-2xl shadow-[0_0_60px_rgba(109,74,255,0.3)] overflow-hidden">
          {/* Animated gradient border */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#6D4AFF] via-[#9D7AFF] to-[#6D4AFF] opacity-20 blur-xl animate-pulse" />
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all z-10"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Content */}
          <div className="relative p-6 sm:p-8 text-center">
            {/* Logo */}
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-5 sm:mb-6">
              <div className="absolute inset-0 bg-[#6D4AFF] rounded-xl sm:rounded-2xl blur-xl opacity-50 animate-pulse" />
              <div className="relative w-full h-full rounded-xl sm:rounded-2xl overflow-hidden border-2 border-[#6D4AFF]/50 shadow-[0_0_30px_rgba(109,74,255,0.4)]">
                <img 
                  src="/mpm-favicon.png" 
                  alt="MyPatriotMail" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Title */}
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#6D4AFF] animate-pulse" />
              <h2 className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                Install App
              </h2>
            </div>

            {/* Description */}
            <p className="text-gray-400 mb-5 text-xs sm:text-sm leading-relaxed px-2">
              Get instant access to your secure inbox with offline support.
            </p>

            {/* Features - 2x2 grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-5 sm:mb-6">
              {[
                { icon: '⚡', text: 'Instant' },
                { icon: '🔒', text: 'Secure' },
                { icon: '📱', text: 'Offline' },
                { icon: '🔔', text: 'Alerts' }
              ].map((feature, i) => (
                <div key={i} className="flex items-center justify-center gap-1.5 bg-[#1a1a2e]/50 rounded-lg px-2 py-2 border border-[#2d2d44]">
                  <span className="text-sm sm:text-base">{feature.icon}</span>
                  <span className="text-[10px] sm:text-xs text-gray-300">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Device-specific instructions for iOS */}
            {deviceType === 'ios' && (
              <div className="bg-[#6D4AFF]/10 border border-[#6D4AFF]/30 rounded-lg p-3 mb-5 text-left">
                <p className="text-xs text-gray-300 mb-1.5">
                  <span className="font-semibold text-white">To install on iOS:</span>
                </p>
                <ol className="text-[10px] sm:text-xs text-gray-400 space-y-0.5 list-decimal list-inside">
                  <li>Tap <span className="text-white">Share</span> <span className="inline-block px-1 bg-[#2d2d44] rounded text-[9px]">⎙</span></li>
                  <li>Tap <span className="text-white">"Add to Home Screen"</span></li>
                </ol>
              </div>
            )}

            {/* Install button */}
            <Button
              onClick={handleInstall}
              className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] hover:from-[#5B3FD9] hover:to-[#7B4FEF] text-white font-bold py-3 sm:py-4 rounded-xl shadow-[0_0_30px_rgba(109,74,255,0.4)] hover:shadow-[0_0_40px_rgba(109,74,255,0.6)] transition-all text-sm sm:text-base group"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:animate-bounce" />
              {deviceType === 'ios' ? 'Got It!' : 'Install App'}
            </Button>

            {/* Skip link */}
            <button
              onClick={handleDismiss}
              className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Maybe later
            </button>

            {/* Device indicator */}
            <div className="mt-4 sm:mt-6 flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-gray-500">
              {deviceType === 'desktop' ? (
                <Monitor className="w-3 h-3 sm:w-4 sm:h-4" />
              ) : (
                <Smartphone className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
              <span>
                {deviceType === 'ios' ? 'iOS' : deviceType === 'android' ? 'Android' : 'Desktop'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: translate(-50%, -50%) scale(0.9);
          }
          to { 
            opacity: 1; 
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
